import React, { useState, useRef, useEffect } from 'react';
import '../App.css';

interface Option {
  id: string;
  label: string;
  subtitle?: string;
}

interface SearchableDropdownProps {
  options: Option[];
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label?: string;
  allowClear?: boolean;
}

const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  options = [],
  value,
  onChange,
  placeholder = 'Search and select...',
  label,
  allowClear = true
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Filter out any invalid options and ensure they have required properties
  const validOptions = (options || []).filter(option => 
    option && 
    typeof option === 'object' && 
    option.id && 
    option.label && 
    typeof option.label === 'string'
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredOptions = validOptions.filter(option =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (option.subtitle && option.subtitle.toLowerCase().includes(searchTerm.toLowerCase())) ||
    option.id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedOption = validOptions.find(opt => opt.id === value);

  const handleSelect = (optionId: string) => {
    onChange(optionId);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange('');
    setSearchTerm('');
  };

  return (
    <div className="form-group" ref={dropdownRef} style={{ position: 'relative' }}>
      {label && <label>{label}</label>}
      <div
        style={{
          position: 'relative',
          width: '100%'
        }}
      >
        <div
          onClick={() => setIsOpen(!isOpen)}
          style={{
            padding: '10px',
            border: '1px solid #E0E0E0',
            borderRadius: '8px',
            cursor: 'pointer',
            backgroundColor: '#fff',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            minHeight: '40px'
          }}
        >
          <span style={{ color: selectedOption ? '#000' : '#9E9E9E' }}>
            {selectedOption ? (
              <div>
                <div style={{ fontWeight: '500' }}>{selectedOption.label}</div>
                {selectedOption.subtitle && (
                  <div style={{ fontSize: '12px', color: '#757575', marginTop: '2px' }}>
                    {selectedOption.subtitle}
                  </div>
                )}
              </div>
            ) : (
              placeholder
            )}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            {allowClear && value && (
              <span
                onClick={handleClear}
                style={{
                  cursor: 'pointer',
                  padding: '2px 6px',
                  borderRadius: '3px',
                  backgroundColor: '#f0f0f0',
                  fontSize: '12px',
                  marginRight: '5px'
                }}
              >
                ✕
              </span>
            )}
            <span style={{ color: '#757575' }}>{isOpen ? '▲' : '▼'}</span>
          </div>
        </div>

        {isOpen && (
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              backgroundColor: '#fff',
              border: '1px solid #E0E0E0',
              borderRadius: '8px',
              marginTop: '4px',
              maxHeight: '300px',
              overflowY: 'auto',
              zIndex: 1050,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}
          >
            <div style={{ padding: '10px', borderBottom: '1px solid #eee' }}>
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                style={{
                  width: '100%',
                  padding: '8px',
                  border: '1px solid #E0E0E0',
                  borderRadius: '8px',
                  fontSize: '14px'
                }}
                autoFocus
              />
            </div>
            <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
              {filteredOptions.length === 0 ? (
                <div style={{ padding: '15px', textAlign: 'center', color: '#9E9E9E' }}>
                  No options found
                </div>
              ) : (
                filteredOptions.map((option) => (
                  <div
                    key={option.id}
                    onClick={() => handleSelect(option.id)}
                    style={{
                      padding: '12px 15px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #f0f0f0',
                      backgroundColor: value === option.id ? '#E8F5E9' : '#fff',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (value !== option.id) {
                        e.currentTarget.style.backgroundColor = '#f5f5f5';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (value !== option.id) {
                        e.currentTarget.style.backgroundColor = '#fff';
                      }
                    }}
                  >
                    <div style={{ fontWeight: '500', marginBottom: option.subtitle ? '4px' : '0' }}>
                      {option.label}
                    </div>
                    {option.subtitle && (
                      <div style={{ fontSize: '12px', color: '#757575' }}>
                        {option.subtitle}
                      </div>
                    )}
                    <div style={{ fontSize: '11px', color: '#9E9E9E', marginTop: '2px' }}>
                      ID: {option.id}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchableDropdown;

